import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { templateService } from '../services/templateService'
import { TemplateFormData, TemplateCategory } from '../types/wordTemplate'
import './TemplateForm.css'

export function TemplateNew() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    category: 'letter',
    s3Key: '',
    fileName: '',
    fileSize: 0,
  })
  const [file, setFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const createMutation = useMutation({
    mutationFn: (data: TemplateFormData) => templateService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      navigate('/templates')
    },
  })

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setIsUploading(true)
    // Validate file type
    if (!selectedFile.name.endsWith('.odt') && !selectedFile.name.endsWith('.ott')) {
      setUploadError('Please select an .odt or .ott file')
      setIsUploading(false)
      return
    }

    // Validate file size (max 25MB)
    if (selectedFile.size > 25 * 1024 * 1024) {
      setUploadError('File size must be less than 25MB')
      setIsUploading(false)
      return
    }

    setFile(selectedFile)
    setUploadError(null)
    setFormData((prev) => ({
      ...prev,
      s3Key: '',
      fileName: '',
      fileSize: 0,
      templateId: undefined,
      format: undefined,
      mimeType: undefined,
    }))

    try {
      const initResponse = await templateService.initUpload(selectedFile)
      await templateService.uploadToS3(initResponse.uploadUrl, initResponse.fields, selectedFile)
      setFormData((prev) => ({
        ...prev,
        templateId: initResponse.templateId,
        s3Key: initResponse.s3Key,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        format: selectedFile.name.endsWith('.odt') ? 'odt' : 'ott',
        mimeType: selectedFile.type || undefined,
      }))
    } catch (error) {
      setUploadError('Error uploading file: ' + String(error))
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      alert('Please enter a template name')
      return
    }

    if (isUploading) {
      alert('Please wait for the upload to finish before submitting')
      return
    }

    if (!formData.s3Key) {
      alert('Please upload an OpenOffice template file (.odt/.ott)')
      return
    }

    try {
      const created = await createMutation.mutateAsync(formData)
      if (!created?.id) {
        alert('Template was uploaded but could not be created. Please try again.')
      }
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Failed to create template')
    }
  }

  const categoryOptions: { value: TemplateCategory; label: string }[] = [
    { value: 'letter', label: 'Letter' },
    { value: 'form', label: 'Form' },
    { value: 'referral', label: 'Referral' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'report', label: 'Report' },
    { value: 'other', label: 'Other' },
  ]

  return (
    <div className="template-form-page">
      <div className="template-form-header">
        <h1>Create New Template</h1>
        <button className="btn-secondary" onClick={() => navigate('/templates')}>
          Cancel
        </button>
      </div>

      <form className="template-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Template Name *</label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Referral Letter Template"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe what this template is used for..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Category *</label>
          <select
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as TemplateCategory })}
            required
          >
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
      <label htmlFor="file">OpenOffice Template File (.odt/.ott) *</label>
          <input
            id="file"
            type="file"
            accept=".odt,.ott"
            onChange={handleFileChange}
            required
          />
          {file && (
            <div className="file-info">
              <span>✓ {file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
            </div>
          )}
          {uploadError && <div className="error-message">{uploadError}</div>}
          <div className="form-help">
            Upload an OpenOffice document (.odt/.ott) with template variables using {'{variable}'} syntax.
            <br />
            Example: {'{patient.firstName}'}, {'{patient.lastName}'}, {'{system.currentDateFormatted}'}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/templates')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={createMutation.isPending || isUploading || !formData.s3Key}>
            {isUploading ? 'Uploading...' : createMutation.isPending ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </form>
    </div>
  )
}
































